
/**
 * Recovery actions for handling critical errors
 * Provides utilities for recovering from error states
 */

import { performAuthCleanup } from '../auth/authCleanup';
import { performEmergencyCleanup } from '../deployment/storageQuotaManager';
import { trackErrorEnhanced } from './enhancedErrorTracker';
import { NetworkStatus } from '../monitoring/networkMonitor';

// Error categories for targeted recovery
export type ErrorCategory = 
  | 'auth' 
  | 'network' 
  | 'storage' 
  | 'rendering' 
  | 'api' 
  | 'unknown';

// Context data for recovery attempts
interface RecoveryContext {
  networkStatus?: NetworkStatus;
  componentName?: string;
  attemptCount?: number;
  [key: string]: any;
}

/**
 * Categorize an error to determine appropriate recovery strategy
 */
export function categorizeError(errorData: Error | string): ErrorCategory {
  const errorMessage = typeof errorData === 'string' 
    ? errorData.toLowerCase()
    : (errorData.message || '').toLowerCase();
  
  // Check for auth-related errors
  if (errorMessage.includes('auth') || 
      errorMessage.includes('session') || 
      errorMessage.includes('token') ||
      errorMessage.includes('login') ||
      errorMessage.includes('permission')) {
    return 'auth';
  }
  
  // Check for network-related errors
  if (errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('connection') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('offline')) {
    return 'network';
  }
  
  // Check for storage-related errors
  if (errorMessage.includes('storage') ||
      errorMessage.includes('quota') ||
      errorMessage.includes('space') ||
      errorMessage.includes('memory')) {
    return 'storage';
  }
  
  // Check for rendering errors
  if (errorMessage.includes('render') ||
      errorMessage.includes('component') ||
      errorMessage.includes('react') ||
      errorMessage.includes('element')) {
    return 'rendering';
  }
  
  // Check for API errors
  if (errorMessage.includes('api') ||
      errorMessage.includes('request') ||
      errorMessage.includes('endpoint') ||
      errorMessage.includes('response') ||
      errorMessage.includes('status')) {
    return 'api';
  }
  
  // Default to unknown
  return 'unknown';
}

/**
 * Attempt auto-recovery based on error data and context
 * @param errorData Error data to guide recovery actions
 * @param context Optional context to guide recovery strategy
 * @returns Boolean indicating if recovery succeeded
 */
export function attemptAutoRecovery(errorData?: any, context: RecoveryContext = {}): boolean {
  console.log('Attempting auto-recovery with data:', errorData);
  let recoverySucceeded = false;
  
  try {
    // Determine error category
    const errorCategory = categorizeError(errorData);
    console.log('Determined error category:', errorCategory);
    
    // Check network status from context
    const hasNetworkIssue = context.networkStatus === NetworkStatus.POOR;
    
    // Apply recovery strategy based on error category
    switch (errorCategory) {
      case 'auth':
        console.log('Detected auth-related error, cleaning up auth state...');
        performAuthCleanup();
        recoverySucceeded = true;
        
        // Log successful auth recovery
        trackErrorEnhanced(
          'Auth recovery completed',
          'RecoveryActions',
          { errorType: 'auth', recoverySucceeded: true }
        );
        break;
        
      case 'storage':
        console.log('Detected storage-related error, performing emergency cleanup...');
        // More aggressive cleanup for storage errors
        const cleanedItems = performEmergencyCleanup(0.85);
        recoverySucceeded = true;
        
        // Log successful storage recovery
        trackErrorEnhanced(
          'Storage cleanup recovery completed',
          'RecoveryActions',
          { 
            errorType: 'storage', 
            recoverySucceeded: true,
            cleanedItems
          }
        );
        break;
        
      case 'network':
        console.log('Detected network-related error');
        if (hasNetworkIssue) {
          // Can't do much for network issues, but let's track it
          trackErrorEnhanced(
            'Network issue detected during recovery',
            'RecoveryActions',
            { networkStatus: context.networkStatus }
          );
          recoverySucceeded = false;
        } else {
          // If network appears okay but we got a network error, 
          // it might be a temporary issue, mark as recovered
          recoverySucceeded = true;
        }
        break;
        
      default:
        // General recovery actions for unknown error types
        console.log('No specific recovery action found, trying general cleanup');
        performAuthCleanup();
        
        // Also try to perform a small storage cleanup just in case
        try {
          const cleanedItems = performEmergencyCleanup(0.7);
          
          // Log general recovery
          trackErrorEnhanced(
            'General recovery completed',
            'RecoveryActions',
            { 
              errorType: 'general',
              cleanedItems
            }
          );
        } catch (e) {
          console.warn('Storage cleanup failed during recovery:', e);
          // Continue recovery process even if this fails
        }
        
        // For unknown errors, let's be optimistic but conservative
        recoverySucceeded = context.attemptCount ? context.attemptCount <= 1 : true;
        break;
    }
  } catch (e) {
    console.warn('Error during recovery attempt:', e);
    recoverySucceeded = false;
    
    // Log recovery failure
    trackErrorEnhanced(
      e,
      'RecoveryActions',
      { operation: 'attemptAutoRecovery', status: 'failed' }
    );
  }
  
  return recoverySucceeded;
}

/**
 * Check for recent errors and attempt recovery
 * @param maxHoursOld Maximum age of errors to recover from
 */
export function recoverFromRecentErrors(maxHoursOld: number = 24): boolean {
  try {
    const lastErrorData = localStorage.getItem('last_error');
    if (lastErrorData) {
      const errorData = JSON.parse(lastErrorData);
      const errorTime = new Date(errorData.timestamp);
      const now = new Date();
      const hoursSinceError = (now.getTime() - errorTime.getTime()) / (1000 * 60 * 60);
      
      // If error is recent, try to clean up state
      if (hoursSinceError < maxHoursOld) {
        console.log('Recent error detected, attempting auto-recovery...');
        return attemptAutoRecovery(errorData);
      }
    }
    
    return false;
  } catch (e) {
    console.warn('Error checking for recent errors:', e);
    
    // Log recovery failure
    trackErrorEnhanced(
      e,
      'RecoveryActions',
      { operation: 'recoverFromRecentErrors', maxHoursOld }
    );
    
    return false;
  }
}

/**
 * Purge all error logs to start fresh
 */
export function purgeErrorLogs(): void {
  try {
    // Get all keys in localStorage
    const allKeys = Object.keys(localStorage);
    
    // Find and remove all error-related entries
    const errorKeys = allKeys.filter(key => 
      key.startsWith('global_exception_') || 
      key.startsWith('error_') || 
      key === 'last_error' || 
      key === 'error_log'
    );
    
    // Remove all found keys
    errorKeys.forEach(key => localStorage.removeItem(key));
    
    console.log(`Purged ${errorKeys.length} error log entries`);
    
    // Log purge operation
    if (errorKeys.length > 0) {
      trackErrorEnhanced(
        'Error logs purged',
        'RecoveryActions',
        { removedLogs: errorKeys.length }
      );
    }
  } catch (e) {
    console.warn('Failed to purge error logs:', e);
    
    // Log purge failure
    trackErrorEnhanced(
      e,
      'RecoveryActions',
      { operation: 'purgeErrorLogs' }
    );
  }
}

export default {
  attemptAutoRecovery,
  recoverFromRecentErrors,
  purgeErrorLogs,
  categorizeError
};
